import { CaretRightIcon } from '@phosphor-icons/react/dist/csr/CaretRight';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { BreadcrumbItem } from './shellState';
import { hrefFor } from '../routing';
import styles from './Breadcrumb.module.css';

/**
 * The topbar breadcrumb. Handoff: "the breadcrumb carries location" — it is what
 * replaces the panel window chrome the old build put on every card.
 *
 * An ordered list inside a named `<nav>` is the WAI-ARIA breadcrumb pattern, and
 * the order is the information: a `<div>` of links reads as a row of unrelated
 * links. The last entry is deliberately NOT a link — a page does not link to
 * itself — and carries `aria-current="page"`.
 *
 * The caret is the one place this shell mirrors an ICON rather than a box.
 * Handoff, "Arabic & RTL": "directional icons flip (carets, arrows, next/prev)".
 * That is done in CSS against `[dir="rtl"]` rather than by picking a different
 * glyph in JS, because a component that forked on direction would need keeping
 * in step by hand at every later call site — the same argument that makes light
 * mode a token remap.
 */

export interface BreadcrumbProps {
  items: readonly BreadcrumbItem[];
  onNavigate?: (path: string) => void;
  className?: string;
}

export function Breadcrumb({ items, onNavigate, className }: BreadcrumbProps) {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  function handleClick(path: string) {
    return (event: MouseEvent<HTMLAnchorElement>) => {
      if (onNavigate === undefined) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (event.button !== 0) return;
      event.preventDefault();
      onNavigate(path);
    };
  }

  return (
    <nav
      aria-label={t('nav.breadcrumb')}
      data-testid="breadcrumb"
      className={[styles['breadcrumb'], className].filter(Boolean).join(' ')}
    >
      <ol className={styles['list']}>
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={`${item.label}-${String(index)}`} className={styles['item']}>
              {last || item.path === undefined ? (
                <span
                  className={last ? styles['current'] : styles['link']}
                  aria-current={last ? 'page' : undefined}
                >
                  {item.label}
                </span>
              ) : (
                <a href={hrefFor(item.path)} className={styles['link']} onClick={handleClick(item.path)}>
                  {item.label}
                </a>
              )}
              {last ? null : (
                <CaretRightIcon
                  aria-hidden="true"
                  data-testid="breadcrumbSeparator"
                  className={styles['separator']}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
